import { api } from '../slices/api';

/**
 * Mill-certificate review queue for Alro and McMaster-Carr.
 *
 * Alro emails a packing slip (an "eREPORT") listing what shipped with an
 * `RT<tag> HT#<heat>` pair per bar, then a separate email per heat carrying
 * the mill certificate as a scanned PDF. A proposal is one heat: the backend
 * pairs the two emails, guesses which ERP material order line the bar belongs
 * to, and waits here for a person to confirm before anything is attached.
 *
 * McMaster certify by PO line instead: a proposal is one line, joined from its
 * packing list and its certificate. A line McMaster sends no certificate for
 * is a supporting document -- its packing list can still be attached.
 */

export type CertVendor = 'alro' | 'mcmaster';

export type ProposalStatus = 'pending' | 'confirmed' | 'rejected';

/** Why a proposal is not simply ready, which is what the reviewer acts on. */
export type ProposalBucket =
	| 'ready'
	| 'waiting_cert'
	| 'orphan_cert'
	| 'no_po'
	/** The identical file is already attached in ERP, so there is nothing to do. */
	| 'already_certified'
	/** A McMaster line with no certificate; Match attaches its packing list. */
	| 'supporting_document';

/** How a certificate ended up, in the register's words. */
export type CertDisposition =
	| 'to_review'
	| 'linked'
	| 'manually_linked'
	| 'ignored'
	| 'failure';

export type MatchMethod = 'edp' | 'type_size_shape' | 'manual' | '';

export interface CertLineItem {
	id: string;
	type_name: string | null;
	category_name: string | null;
	shape_name: string | null;
	vendor_part_number: string | null;
	quantity_ordered: number | null;
	length: string | null;
	height: string | null;
	width: string | null;
	outer_diameter: string | null;
	inner_diameter: string | null;
	wall_thickness: string | null;
	status: string | null;
}

/**
 * A purchase order in the manual picker.
 *
 * Both numbers are shown: `purchase_order_name` is the label the vendor quotes
 * (`PO-5646`), `purchase_order_number` an unrelated internal sequence.
 */
export interface CertOrderBrief {
	id: string;
	purchase_order_name: string | null;
	purchase_order_number: string | null;
	vendor_order_number: string | null;
	vendor_name: string | null;
	/** Which plant the PO was raised for: West Bend or Cedarburg. */
	building_name: string;
	status: string | null;
	created_time: string | null;
	line_item_count: number;
}

export interface CertProposal {
	id: string;
	vendor_key: CertVendor;
	po: string;
	/** Alro's bar tag, e.g. RT12167541. */
	rt: string;
	rt_digits: string;
	/** Mill heat / lot number. */
	heat: string;
	alro_order: string;
	status: ProposalStatus;
	bucket: ProposalBucket;
	match_method: MatchMethod;
	/** Plain-language reasons the matcher landed where it did. */
	mismatch_notes: string[];
	/** More than one heat on the same packing-slip line. */
	mixed_heat: boolean;
	inventory_exists: boolean;
	description: string;
	/** Alro's EDP# or McMaster's catalog number (SKU): the strongest join key. */
	edp: string;
	/** McMaster PO line number; null for Alro. */
	line_number: number | null;
	/** McMaster's certificate number, e.g. 374-6750. */
	cert_number: string;
	/**
	 * A cert Alro sent on request: the subject names a PO and an EDP or part
	 * code but no tag or heat, so there is no slip line and the reviewer picks.
	 */
	requested: boolean;
	/** Short part code from a requested cert's subject; a prefix of the EDP. */
	part_hint: string;
	packing_slip: string;
	qty: string | null;
	length_raw: string;
	/** Parsed dimensions off the packing slip, for comparing against the ERP line. */
	length_inches: string | null;
	height: string | null;
	width: string | null;
	outer_diameter: string | null;
	inner_diameter: string | null;
	wall_thickness: string | null;
	type_token: string;
	shape: string;
	/** When the material order was raised in ERP. */
	order_date: string | null;
	/** The plant the bar was bought for; empty when the PO has not resolved. */
	building_name: string;
	received_at: string | null;
	from_header: string;
	subject: string;
	has_packing_slip_pdf: boolean;
	has_certificate_pdf: boolean;
	/** Degrees to turn the scan so it reads upright; null when undetermined. */
	certificate_rotation: number | null;
	line_item: CertLineItem | null;
	/** This Match marked the ERP line "no mill certificate available". */
	marked_certificate_not_available: boolean;
	/** The ERP line is marked "no mill certificate available", by anyone. */
	line_certificate_not_available: boolean;
	/** The ERP line already has a real mill certificate attached. */
	line_has_material_certificate: boolean;
	/** Only on the confirm response: a real cert cleared the line's flag. */
	certificate_not_available_cleared?: boolean;
	packet_layer_sync_failed?: boolean;
	/** ERP bar IDs (MaterialLength.friendly_id) on the matched line. */
	bar_ids: string[];
	material_order: string | null;
	material_order_line_item: string | null;
	material: string | null;
	confirmed_by_email: string | null;
	confirmed_at: string | null;
	attached_certificate: string | null;
	/** A stored certificate with the same bytes; set when nothing needs linking. */
	duplicate_certificate: string | null;
}

export interface CertPoGroup {
	po: string;
	pending_count: number;
	ready_count: number;
	waiting_cert_count: number;
	orphan_cert_count: number;
	no_po_count: number;
	already_certified_count: number;
	supporting_document_count: number;
	/** Every vendor with a document on this PO. */
	vendor_keys: CertVendor[];
	confirmed_count: number;
	rejected_count: number;
	/** Which plant the PO was raised for: West Bend or Cedarburg. */
	building_name: string;
	oldest_received_at: string | null;
	inventory_exists: boolean;
	mixed_heat: boolean;
}

export interface CertSummary {
	pending_count: number;
	pending_po_count: number;
	ready_count: number;
}

export interface CertUploadResult {
	created: number;
	skipped: number;
	errors: { filename: string; detail: string }[];
}

export interface ProposalQuery {
	po?: string;
	bucket?: ProposalBucket;
	status?: ProposalStatus;
	/** Rejected heats are out of the queue unless asked for by name. */
	include_rejected?: boolean;
	page_size?: number;
}

/**
 * One row in the master register: every mill certificate that arrived by
 * email, and what became of it. Wider than the review queue on purpose --
 * certificates that never parsed far enough to become a proposal are exactly
 * the ones an audit needs to find.
 */
export interface CertRegisterEntry {
	id: string;
	po: string;
	rt: string;
	heat: string;
	received_at: string | null;
	subject: string;
	filename: string;
	content_sha256: string;
	disposition: CertDisposition;
	disposition_label: string;
	disposition_detail: string;
	proposal: string | null;
	proposal_bucket: string;
	certificate_file: string | null;
	material_order_line_item: string | null;
	line_item: CertLineItem | null;
	resolved_by_email: string | null;
	resolved_at: string | null;
	has_pdf: boolean;
}

export interface RegisterQuery {
	disposition?: CertDisposition;
	po?: string;
	search?: string;
	page?: number;
	page_size?: number;
}

export interface RegisterSummary {
	total: number;
	counts: Record<CertDisposition, number>;
}

export type FetchRunStatus = 'queued' | 'running' | 'succeeded' | 'failed';

/** One requested sweep of the mailbox, and how far it got. */
export interface MailboxFetchRun {
	id: string;
	after: string | null;
	before: string | null;
	retry_failed: boolean;
	query: string;
	status: FetchRunStatus;
	detail: string;
	examined_count: number;
	ingested_count: number;
	skipped_count: number;
	failed_count: number;
	started_at: string | null;
	finished_at: string | null;
	requested_by_email: string | null;
	created_time: string | null;
}

/** Mail that was read but did not fully make it into the queue. */
export interface CertMessageWarning {
	id: string;
	subject: string;
	from_header: string;
	received_at: string | null;
	status: 'parse_warning' | 'failed';
	status_detail: string;
}

interface Paginated<T> {
	count: number;
	next: string | null;
	previous: string | null;
	results: T[];
}

const BASE = 'material-certs/proposals';
const REGISTER = 'material-certs/register';

export const materialCertsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * POs with certificate work, aggregated.
		 *
		 * `search` matches the heats first and aggregates what is left, so a PO
		 * can be found by anything written on one of its bars -- heat, RT tag,
		 * EDP, Alro order -- not just its own number.
		 */
		getCertPoGroups: builder.query<
			CertPoGroup[],
			{ status?: ProposalStatus; search?: string } | void
		>({
			query: (params) => {
				const query = new URLSearchParams();
				if (params && 'status' in params && params.status) {
					query.set('status', params.status);
				}
				if (params && 'search' in params && params.search) {
					query.set('search', params.search);
				}
				const suffix = query.toString();
				return `${BASE}/po-groups/${suffix ? `?${suffix}` : ''}`;
			},
			providesTags: ['MaterialCertProposal'],
		}),

		getCertSummary: builder.query<CertSummary, void>({
			query: () => `${BASE}/summary/`,
			providesTags: ['MaterialCertProposal'],
		}),

		getCertProposals: builder.query<CertProposal[], ProposalQuery>({
			query: ({ po, bucket, status, include_rejected, page_size = 500 }) => {
				const params = new URLSearchParams();
				if (po) params.set('po', po);
				if (bucket) params.set('bucket', bucket);
				if (status) params.set('status', status);
				if (include_rejected) params.set('include_rejected', '1');
				params.set('page_size', String(page_size));
				return `${BASE}/?${params.toString()}`;
			},
			transformResponse: (response: Paginated<CertProposal>) => response.results,
			providesTags: ['MaterialCertProposal'],
		}),

		/**
		 * ERP lines to reassign a heat onto.
		 *
		 * Defaults to the matched PO. Pass `materialOrderId` to list the lines of
		 * a PO the reviewer found by hand, which is the only route through when
		 * the number never resolved.
		 */
		getCertProposalLineItems: builder.query<
			CertLineItem[],
			{ id: string; materialOrderId?: string | null }
		>({
			query: ({ id, materialOrderId }) =>
				materialOrderId
					? `${BASE}/${id}/line-items/?material_order_id=${materialOrderId}`
					: `${BASE}/${id}/line-items/`,
		}),

		/** Look up a purchase order by either of its numbers. */
		searchCertPos: builder.query<CertOrderBrief[], string>({
			query: (q) => `${BASE}/po-search/?q=${encodeURIComponent(q)}`,
		}),

		uploadCertFiles: builder.mutation<CertUploadResult, FormData>({
			query: (formData) => ({
				url: `${BASE}/upload/`,
				method: 'POST',
				body: formData,
			}),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		confirmCertProposal: builder.mutation<
			CertProposal,
			{
				id: string;
				material_order_line_item_id?: string;
				/** Typed off the scan for a requested cert, which has no heat. */
				heat_lot_number?: string;
				/** Supporting documents only: the material will never have a cert. */
				mark_certificate_not_available?: boolean;
			}
		>({
			query: ({
				id,
				material_order_line_item_id,
				heat_lot_number,
				mark_certificate_not_available,
			}) => ({
				url: `${BASE}/${id}/confirm/`,
				method: 'POST',
				body: {
					...(material_order_line_item_id ? { material_order_line_item_id } : {}),
					...(heat_lot_number ? { heat_lot_number } : {}),
					...(mark_certificate_not_available
						? { mark_certificate_not_available: true }
						: {}),
				},
			}),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		/** Set the recommended ERP line. Match still attaches. */
		assignCertProposal: builder.mutation<
			CertProposal,
			{ id: string; material_order_line_item_id: string }
		>({
			query: ({ id, material_order_line_item_id }) => ({
				url: `${BASE}/${id}/assign/`,
				method: 'POST',
				body: { material_order_line_item_id },
			}),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		rejectCertProposal: builder.mutation<CertProposal, string>({
			query: (id) => ({ url: `${BASE}/${id}/reject/`, method: 'POST' }),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		/** Put a rejected heat back in the queue, rematched against ERP as it is now. */
		restoreCertProposal: builder.mutation<CertProposal, string>({
			query: (id) => ({ url: `${BASE}/${id}/restore/`, method: 'POST' }),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		undoCertProposal: builder.mutation<CertProposal, string>({
			query: (id) => ({ url: `${BASE}/${id}/undo/`, method: 'POST' }),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		/** Re-run the match after the PO or its lines changed in ERP. */
		rematchCertProposal: builder.mutation<CertProposal, string>({
			query: (id) => ({ url: `${BASE}/${id}/rematch/`, method: 'POST' }),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		confirmReadyCerts: builder.mutation<
			{
				confirmed: CertProposal[];
				errors: { id: string; detail: string }[];
				packet_layer_sync_failed?: boolean;
			},
			string
		>({
			query: (po) => ({
				url: `${BASE}/confirm-ready/`,
				method: 'POST',
				body: { po },
			}),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		/**
		 * The preview panes fetch through RTK Query rather than pointing an
		 * iframe at the URL: these endpoints need the bearer token, which an
		 * iframe src cannot carry.
		 */
		getCertProposalPdf: builder.query<
			Blob,
			{ id: string; kind: 'certificate' | 'packing-slip' }
		>({
			query: ({ id, kind }) => ({
				url: `${BASE}/${id}/${kind}-pdf/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),

		getCertRegister: builder.query<Paginated<CertRegisterEntry>, RegisterQuery>({
			query: ({ disposition, po, search, page = 1, page_size = 50 }) => {
				const params = new URLSearchParams();
				if (disposition) params.set('disposition', disposition);
				if (po) params.set('po', po);
				if (search) params.set('search', search);
				params.set('page', String(page));
				params.set('page_size', String(page_size));
				return `${REGISTER}/?${params.toString()}`;
			},
			providesTags: ['MaterialCertProposal'],
		}),

		getCertRegisterSummary: builder.query<RegisterSummary, void>({
			query: () => `${REGISTER}/summary/`,
			providesTags: ['MaterialCertProposal'],
		}),

		getCertRegisterPdf: builder.query<Blob, string>({
			query: (id) => ({
				url: `${REGISTER}/${id}/pdf/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),

		/**
		 * Ask for a date range to be swept out of the mailbox. Answers with the
		 * run row rather than the result: the sweep happens on a worker, and the
		 * run is what the panel polls.
		 */
		fetchCertRange: builder.mutation<
			MailboxFetchRun,
			{ after: string; before: string; retry_failed?: boolean }
		>({
			query: (body) => ({ url: `${BASE}/fetch/`, method: 'POST', body }),
			invalidatesTags: ['MaterialCertProposal'],
		}),

		getCertFetchRuns: builder.query<MailboxFetchRun[], void>({
			query: () => `${BASE}/fetch-runs/`,
			providesTags: ['MaterialCertProposal'],
		}),

		getCertMessageWarnings: builder.query<CertMessageWarning[], void>({
			query: () => `${BASE}/message-warnings/`,
			providesTags: ['MaterialCertProposal'],
		}),
	}),
});

export const {
	useGetCertPoGroupsQuery,
	useGetCertSummaryQuery,
	useGetCertProposalsQuery,
	useGetCertProposalLineItemsQuery,
	useSearchCertPosQuery,
	useUploadCertFilesMutation,
	useConfirmCertProposalMutation,
	useAssignCertProposalMutation,
	useRejectCertProposalMutation,
	useRestoreCertProposalMutation,
	useUndoCertProposalMutation,
	useRematchCertProposalMutation,
	useConfirmReadyCertsMutation,
	useGetCertProposalPdfQuery,
	useGetCertRegisterQuery,
	useGetCertRegisterSummaryQuery,
	useGetCertRegisterPdfQuery,
	useFetchCertRangeMutation,
	useGetCertFetchRunsQuery,
	useGetCertMessageWarningsQuery,
} = materialCertsApi;
